import { useMemo, useState, useRef, useEffect } from 'react';
import {
  projectMarks,
  canonicalizeMarkIds,
  projectGeoPoint,
  computeTurnAngle,
  computeSegmentOffsets,
  offsetLinePoints,
  markColor,
  MARK_RADIUS,
  ARC_STROKE,
  // Belt-tangent mode
  ringRadius,
  beltTangent,
  beltArcPath,
  // Classic mode
  tangentPoint,
  roundingArcPath,
  arcRadius,
  computeVisitPositions,
} from '../lib/coursePreviewLayout.js';
import { bearingDeg, offsetLineBearing, fixedMarkLineBearing } from '../lib/geo.js';
import { DIRECTION_LETTERS, DIRECTION_BEARINGS } from '../lib/courseConstants.js';

const LABEL_FONT_SIZE = 12;
const ANGLE_FONT_SIZE = 10;

// --- Map tile helpers ---

/** Convert lat/lng to OSM tile coordinates at a given zoom. */
function latLngToTile(lat, lng, zoom) {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { x, y };
}

/** Convert OSM tile coords back to lat/lng (NW corner of tile). */
function tileToLatLng(tx, ty, zoom) {
  const n = Math.pow(2, zoom);
  const lng = tx / n * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / n)));
  return { lat: latRad * 180 / Math.PI, lng };
}

/** Inverse of projectGeoPoint: SVG pixel → lat/lng. */
function svgToLatLng(svgX, svgY, transform) {
  const { scale, offsetX, offsetY, minX, minY, refLat, refLon, cosLat } = transform;
  const projX = (svgX - offsetX) / scale + minX;
  const projY = (svgY - offsetY) / scale + minY;
  const lng = projX / (60 * cosLat) + refLon;
  const lat = -projY / 60 + refLat;
  return { lat, lng };
}

/** Pick a zoom level so the area is covered by roughly 2-4 tiles on each axis. */
function pickZoom(latMin, latMax, lngMin, lngMax) {
  const latRange = latMax - latMin;
  const lngRange = lngMax - lngMin;
  // Approximate: 360° / 2^z degrees per tile (longitude)
  for (let z = 18; z >= 1; z--) {
    const degsPerTile = 360 / Math.pow(2, z);
    const tilesX = lngRange / degsPerTile;
    const tilesY = latRange / degsPerTile; // rough approx
    if (tilesX <= 5 && tilesY <= 5) return z;
  }
  return 1;
}

/**
 * Schematic vector diagram of a sailing course.
 * Shows marks as labeled dots, legs as lines, exaggerated roundings,
 * and bearing-difference annotations with color coding.
 *
 * Supports two rendering modes toggled by a style button:
 * - "belt" (default): belt-and-pulley tangent routing with concentric rings
 * - "classic": simple nearest-point tangent with visit-offset positions
 */
// --- Animation helpers ---

const MIN_OPACITY = 0.35;
const WINDOW_SIZE = 3;
const PER_MARK_MS = 1000;
const HOLD_MS = 2000;
const FADE_OUT_MS = 650;
const PAUSE_MS = 650;
const FADE_IN_MS = 650;

/** Opacity for sequence index i given animation head and blend.
 *  When highlightIdx is set, shows that mark + adjacent legs at full opacity. */
function opacityForIndex(i, animHead, blend, n, highlightIdx) {
  // Highlight mode: selected mark + neighbors bright, rest dimmed
  if (highlightIdx != null) {
    const dist = Math.abs(i - highlightIdx);
    if (dist <= 1) return 1.0;
    return MIN_OPACITY;
  }

  // Hold phase: everything at full opacity
  if (animHead < 0) return 1.0;
  if (n === 0) return 1.0;

  const dist = animHead - i;
  let raw;
  if (dist < 0) {
    raw = MIN_OPACITY;
  } else if (dist < WINDOW_SIZE) {
    raw = 1.0;
  } else if (dist < WINDOW_SIZE + 0.5) {
    const fade = (dist - WINDOW_SIZE) / 0.5;
    raw = Math.max(MIN_OPACITY, 1.0 - (1.0 - MIN_OPACITY) * fade);
  } else {
    raw = MIN_OPACITY;
  }
  return 1.0 - blend * (1.0 - raw);
}

/** Sweep fraction for rounding arc at index i. */
function sweepFractionForIndex(i, animHead, blend, highlightIdx) {
  // Highlight mode: all arcs fully drawn
  if (highlightIdx != null) return 1.0;
  if (animHead < 0 || blend < 1.0) return 1.0;
  const frac = animHead - i;
  if (frac >= 1.0) return 1.0;
  if (frac < 0) return 0.0;
  return Math.min(1.0, Math.max(0.0, frac));
}

/** Best opacity for a unique mark across all its sequence appearances. */
function opacityForMark(markId, markIds, animHead, blend, highlightIdx) {
  let best = MIN_OPACITY;
  for (let i = 0; i < markIds.length; i++) {
    if (markIds[i] === markId) {
      const o = opacityForIndex(i, animHead, blend, markIds.length, highlightIdx);
      if (o > best) best = o;
    }
  }
  return best;
}

/** 2D cross product (z-component). */
function cross2(a, b) {
  return a.x * b.y - a.y * b.x;
}

/**
 * Intersect the infinite line through `o` with direction `d` against the
 * infinite line through `a`→`b`. Returns the point plus `s`, the parameter
 * along `a`→`b` (0..1 = within the a–b segment, outside = beyond an end). Null
 * when parallel. (ra-etwi #3)
 */
function lineLineIntersect(o, d, a, b) {
  const e = { x: b.x - a.x, y: b.y - a.y };
  const denom = cross2(d, e);
  if (Math.abs(denom) < 1e-9) return null; // parallel
  const w = { x: a.x - o.x, y: a.y - o.y };
  const t = cross2(w, e) / denom;
  const s = cross2(w, d) / denom;
  return { point: { x: o.x + t * d.x, y: o.y + t * d.y }, s };
}

/**
 * Wind-aligned (rhumb) axis (unit vector) from a mark toward a start/finish
 * line. Offset lines carry the configured bearing as `line.axis`; otherwise
 * fall back to the direction toward the line's centre. (ra-etwi #3)
 */
function sfAxis(line, markPos) {
  if (line.axis) return line.axis;
  const cx = (line.p1.x + line.p2.x) / 2;
  const cy = (line.p1.y + line.p2.y) / 2;
  const dx = cx - markPos.x, dy = cy - markPos.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

export default function CoursePreview({
  marks = [],
  markIds: rawMarkIds,
  markDirections,
  bearing,
  startFinish,
  onMarkClick,
  highlightedIndex,
  onClearHighlight,
  width = 500,
  height = 400,
}) {
  const [mode, setMode] = useState('belt');
  const [showMap, setShowMap] = useState(true);
  const [showAngles, setShowAngles] = useState(true);
  const isBelt = mode === 'belt';

  // --- Animation state ---
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const [animHead, setAnimHead] = useState(-1);
  const [animBlend, setAnimBlend] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  const [playing, setPlaying] = useState(false); // true during single-shot or autoplay

  // Animation loop
  const markIdsForAnim = Array.isArray(rawMarkIds) ? rawMarkIds : [];
  const n = markIdsForAnim.length;

  function stopAnim() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startTimeRef.current = null;
    setAnimHead(-1);
    setAnimBlend(0);
    setPlaying(false);
  }

  function startAnim(loop) {
    stopAnim();
    if (n === 0) return;
    setPlaying(true);

    const sweepMs = PER_MARK_MS * Math.max(n, 1);
    const total = HOLD_MS + FADE_OUT_MS + PAUSE_MS + sweepMs + FADE_IN_MS;

    function tick(timestamp) {
      if (startTimeRef.current == null) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;

      // Single-shot: stop after one full cycle
      if (!loop && elapsed >= total) {
        setAnimHead(-1);
        setAnimBlend(0);
        setPlaying(false);
        rafRef.current = null;
        return;
      }

      const v = (elapsed % total) / total;
      const holdEnd = HOLD_MS / total;
      const fadeOutEnd = holdEnd + FADE_OUT_MS / total;
      const pauseEnd = fadeOutEnd + PAUSE_MS / total;
      const sweepEnd = pauseEnd + sweepMs / total;

      let head, blend;
      if (v < holdEnd) {
        head = -1;
        blend = 0;
      } else if (v < fadeOutEnd) {
        head = 0;
        blend = Math.min(1, Math.max(0, (v - holdEnd) / (fadeOutEnd - holdEnd)));
      } else if (v < pauseEnd) {
        head = 0;
        blend = 1.0;
      } else if (v < sweepEnd) {
        head = ((v - pauseEnd) / (sweepEnd - pauseEnd)) * n;
        blend = 1.0;
      } else {
        head = n;
        blend = Math.min(1, Math.max(0, 1.0 - (v - sweepEnd) / (1.0 - sweepEnd)));
      }

      setAnimHead(head);
      setAnimBlend(blend);
      rafRef.current = requestAnimationFrame(tick);
    }

    startTimeRef.current = null;
    rafRef.current = requestAnimationFrame(tick);
  }

  // React to autoplay toggle and highlight changes
  useEffect(() => {
    if (highlightedIndex != null) {
      setAutoplay(false);
      stopAnim();
      return;
    }
    if (autoplay) {
      startAnim(true);
    } else {
      stopAnim();
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, highlightedIndex, autoplay]);

  // Resolve course markIds to the marks' canonical id, case-insensitively, at the
  // single point all lookups below (markLookup / projectMarks / positions) flow
  // through. Fixes the P0 where CourseEditor-saved (lowercased) ids missed every
  // mark lookup → "No marks with coordinates". (ra-or1y, found by Chris.)
  const markIds = useMemo(
    () => canonicalizeMarkIds(rawMarkIds, marks),
    [rawMarkIds, marks],
  );
  // Build mark lookup for bearing calculations
  const markLookup = useMemo(() => {
    const lookup = {};
    for (const m of marks) {
      if (typeof m.latitude === 'number' && typeof m.longitude === 'number' &&
          !(m.latitude === 0 && m.longitude === 0)) {
        lookup[m.id] = [m.latitude, m.longitude];
      }
    }
    return lookup;
  }, [marks]);

  // Project marks to SVG coordinates (extra padding for start/finish lines)
  const padding = startFinish ? 55 : 40;
  const projection = useMemo(
    () => projectMarks(marks, markIds, width, height, padding),
    [marks, markIds, width, height, padding],
  );

  // Compute leg bearings and turn angles
  const legData = useMemo(() => {
    const legs = [];
    for (let i = 0; i < markIds.length - 1; i++) {
      const posA = markLookup[markIds[i]];
      const posB = markLookup[markIds[i + 1]];
      if (posA && posB) {
        legs.push({
          from: markIds[i],
          to: markIds[i + 1],
          bearing: bearingDeg(posA[0], posA[1], posB[0], posB[1]),
        });
      } else {
        legs.push({ from: markIds[i], to: markIds[i + 1], bearing: null });
      }
    }
    return legs;
  }, [markIds, markLookup]);

  // Compute turn angles at intermediate marks (first/last filled in later with start/finish positions)
  const baseTurnAngles = useMemo(() => {
    const angles = new Array(markIds.length).fill(null);
    for (let i = 1; i < markIds.length - 1; i++) {
      const inLeg = legData[i - 1];
      const outLeg = legData[i];
      if (inLeg.bearing !== null && outLeg.bearing !== null) {
        angles[i] = computeTurnAngle(inLeg.bearing, outLeg.bearing);
      }
    }
    return angles;
  }, [markIds, legData]);

  // Segment offsets for repeated legs
  const segmentOffsets = useMemo(
    () => computeSegmentOffsets(markIds),
    [markIds],
  );

  // Visit count per mark for arc radius scaling
  const visitCounts = useMemo(() => {
    const counts = {};
    const perIndex = [];
    for (const id of markIds) {
      counts[id] = (counts[id] || 0) + 1;
      perIndex.push(counts[id]);
    }
    return perIndex;
  }, [markIds]);

  const { positions, sequencePositions } = projection;

  // Classic mode: per-visit offset positions for revisited marks
  const visitPositions = useMemo(
    () => computeVisitPositions(positions, markIds),
    [positions, markIds],
  );

  // Start/finish line geometry
  const startFinishLines = useMemo(() => {
    if (!startFinish || sequencePositions.length < 2) return { start: null, finish: null };
    const LINE_HALF = 30;
    const LABEL_GAP = 14;
    const { transform } = projection;
    // Projected space is in nautical miles; transform.scale is pixels-per-nm
    // (see projectGeoPoint in coursePreviewLayout.js). Convert a configured
    // offset distance to a pixel distance so the preview tracks the nm value.
    // Falls back to the legacy hardcoded sizing when scale/offset is unavailable.
    const scale = typeof transform?.scale === 'number' ? transform.scale : null;
    const offsetPx = (nm, fallback) =>
      scale !== null && typeof nm === 'number' ? nm * scale : fallback;

    function computeFixed(markAId, markBId) {
      const markACoord = markLookup[markAId];
      const markBCoord = markLookup[markBId];
      if (!markACoord || !markBCoord || !transform) return null;
      const posA = projectGeoPoint(markACoord[0], markACoord[1], transform);
      const posB = projectGeoPoint(markBCoord[0], markBCoord[1], transform);
      const mid = { x: (posA.x + posB.x) / 2, y: (posA.y + posB.y) / 2 };
      const dx = posB.x - posA.x;
      const dy = posB.y - posA.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const nx = len > 0 ? -dy / len : 0;
      const ny = len > 0 ? dx / len : -1;
      return {
        mode: 'fixed',
        p1: posA,
        p2: posB,
        markA: { pos: posA, id: markAId },
        markB: { pos: posB, id: markBId },
        label: { x: mid.x + nx * LABEL_GAP, y: mid.y + ny * LABEL_GAP },
      };
    }

    // A line that sits ON a single fixed mark (markA), oriented perpendicular to
    // an axis bearing (numeric offsetAngle when set, else the course's
    // approach/departure direction), placed offsetNm from the mark (0 = at the
    // mark). Geometry mirrors the offset branch but anchored at markA, so it
    // renders even when no wind/direction is configured. (ra-gbrd.22)
    function computeFixedMark(markAId, offsetNm, offsetAngle, isStart) {
      const markACoord = markLookup[markAId];
      if (!markACoord || !transform) return null;
      const anchor = projectGeoPoint(markACoord[0], markACoord[1], transform);
      const numeric = Number(offsetAngle);
      const axisBrg = Number.isFinite(numeric)
        ? ((numeric % 360) + 360) % 360
        : fixedMarkLineBearing(markIds, markLookup, markAId, isStart);
      if (axisBrg == null) return null;
      const dirRad = (axisBrg - 90) * Math.PI / 180; // compass → SVG (0°=up)
      const sectorR = offsetPx(typeof offsetNm === 'number' ? offsetNm : 0, 0);
      const cx = anchor.x + sectorR * Math.cos(dirRad);
      const cy = anchor.y + sectorR * Math.sin(dirRad);
      const px = -Math.sin(dirRad);
      const py = Math.cos(dirRad);
      return {
        mode: 'offset',
        axis: { x: Math.cos(dirRad), y: Math.sin(dirRad) },
        p1: { x: cx + px * LINE_HALF, y: cy + py * LINE_HALF },
        p2: { x: cx - px * LINE_HALF, y: cy - py * LINE_HALF },
        label: { x: cx + Math.cos(dirRad) * LABEL_GAP, y: cy + Math.sin(dirRad) * LABEL_GAP },
      };
    }

    function computeStartLine() {
      if (startFinish.startType === 'fixed') {
        return computeFixed(startFinish.startMarkA, startFinish.startMarkB);
      }
      if (startFinish.startType === 'fixed_mark') {
        return computeFixedMark(startFinish.startMarkA, startFinish.startOffsetNm, startFinish.startOffsetAngle, true);
      }
      // Offset: line perpendicular to the configured offset bearing, placed
      // that bearing away from the first mark. Honors startOffsetAngle (the
      // downwind/upwind keyword or a numeric bearing) instead of always drawing
      // downwind; matches the Course Viewer and the leg table. (ra-etwi)
      const firstPos = sequencePositions[0];
      if (typeof bearing === 'number') {
        const lineBrg = offsetLineBearing(startFinish.startOffsetAngle, bearing, true);
        const dirRad = (lineBrg - 90) * Math.PI / 180; // compass → SVG (0°=up)
        const sectorR = offsetPx(startFinish.startOffsetNm, Math.min(width, height) * 0.2);
        const cx = firstPos.x + sectorR * Math.cos(dirRad);
        const cy = firstPos.y + sectorR * Math.sin(dirRad);
        const px = -Math.sin(dirRad);
        const py = Math.cos(dirRad);
        return {
          mode: 'offset',
          axis: { x: Math.cos(dirRad), y: Math.sin(dirRad) },
          p1: { x: cx + px * LINE_HALF, y: cy + py * LINE_HALF },
          p2: { x: cx - px * LINE_HALF, y: cy - py * LINE_HALF },
          label: { x: cx + Math.cos(dirRad) * LABEL_GAP, y: cy + Math.sin(dirRad) * LABEL_GAP },
        };
      }
      // No bearing — fall back to perpendicular to first leg
      const secondPos = sequencePositions[1];
      const dx = secondPos.x - firstPos.x;
      const dy = secondPos.y - firstPos.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return null;
      const ux = dx / len, uy = dy / len;
      const px = -uy, py = ux;
      const gap = offsetPx(startFinish.startOffsetNm, 30);
      const cx = firstPos.x - ux * gap;
      const cy = firstPos.y - uy * gap;
      return {
        mode: 'offset',
        axis: { x: -ux, y: -uy },
        p1: { x: cx + px * LINE_HALF, y: cy + py * LINE_HALF },
        p2: { x: cx - px * LINE_HALF, y: cy - py * LINE_HALF },
        label: { x: cx - ux * LABEL_GAP, y: cy - uy * LABEL_GAP },
      };
    }

    function computeFinishLine() {
      if (startFinish.finishType === 'fixed') {
        return computeFixed(startFinish.finishMarkA, startFinish.finishMarkB);
      }
      if (startFinish.finishType === 'fixed_mark') {
        return computeFixedMark(startFinish.finishMarkA, startFinish.finishOffsetNm, startFinish.finishOffsetAngle, false);
      }
      const lastPos = sequencePositions[sequencePositions.length - 1];
      // Offset: line perpendicular to the configured offset bearing, placed
      // that bearing away from the last mark. Honors finishOffsetAngle (the
      // upwind/downwind keyword or a numeric bearing) instead of always drawing
      // along the final leg; matches the Course Viewer and the leg table.
      // (ra-etwi)
      if (typeof bearing === 'number') {
        const lineBrg = offsetLineBearing(startFinish.finishOffsetAngle, bearing, false);
        const dirRad = (lineBrg - 90) * Math.PI / 180; // compass → SVG (0°=up)
        const sectorR = offsetPx(startFinish.finishOffsetNm, Math.min(width, height) * 0.2);
        const cx = lastPos.x + sectorR * Math.cos(dirRad);
        const cy = lastPos.y + sectorR * Math.sin(dirRad);
        const px = -Math.sin(dirRad);
        const py = Math.cos(dirRad);
        return {
          mode: 'offset',
          axis: { x: Math.cos(dirRad), y: Math.sin(dirRad) },
          p1: { x: cx + px * LINE_HALF, y: cy + py * LINE_HALF },
          p2: { x: cx - px * LINE_HALF, y: cy - py * LINE_HALF },
          label: { x: cx + Math.cos(dirRad) * LABEL_GAP, y: cy + Math.sin(dirRad) * LABEL_GAP },
        };
      }
      // No bearing — fall back to perpendicular to the last leg, beyond last mark
      const prevPos = sequencePositions[sequencePositions.length - 2];
      const dx = lastPos.x - prevPos.x;
      const dy = lastPos.y - prevPos.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return null;
      const ux = dx / len, uy = dy / len;
      const px = -uy, py = ux;
      const gap = offsetPx(startFinish.finishOffsetNm, 30);
      const cx = lastPos.x + ux * gap;
      const cy = lastPos.y + uy * gap;
      return {
        mode: 'offset',
        axis: { x: ux, y: uy },
        p1: { x: cx + px * LINE_HALF, y: cy + py * LINE_HALF },
        p2: { x: cx - px * LINE_HALF, y: cy - py * LINE_HALF },
        label: { x: cx + ux * LABEL_GAP, y: cy + uy * LABEL_GAP },
      };
    }

    const start = computeStartLine();
    const finish = computeFinishLine();

    // Detect same physical line → offset dashes and separate labels
    if (start && finish) {
      let sameLine = false;
      if (start.mode === 'fixed' && finish.mode === 'fixed') {
        sameLine = (
          (start.markA?.id === finish.markA?.id && start.markB?.id === finish.markB?.id) ||
          (start.markA?.id === finish.markB?.id && start.markB?.id === finish.markA?.id)
        );
      } else if (start.mode !== 'fixed' && finish.mode !== 'fixed') {
        const d = Math.hypot(start.p1.x - finish.p1.x, start.p1.y - finish.p1.y);
        sameLine = d < 10;
      }
      if (sameLine) {
        // Place icons at opposite ends of the line
        start.iconPos = { ...start.p1 };
        finish.iconPos = { ...start.p2 };
        start.sameLine = true;
        finish.sameLine = true;
        finish.dashOffset = 8;
      }
    }

    return { start, finish };
  }, [startFinish, sequencePositions, markLookup, bearing, width, height, projection]);

  // Map tile data: compute which OSM tiles cover the SVG viewport
  const mapTiles = useMemo(() => {
    if (!showMap || !projection.transform) return [];
    const { transform } = projection;

    // Get lat/lng of SVG corners
    const nw = svgToLatLng(0, 0, transform);
    const se = svgToLatLng(width, height, transform);
    const latMin = Math.min(nw.lat, se.lat);
    const latMax = Math.max(nw.lat, se.lat);
    const lngMin = Math.min(nw.lng, se.lng);
    const lngMax = Math.max(nw.lng, se.lng);

    const zoom = pickZoom(latMin, latMax, lngMin, lngMax);
    const tileNW = latLngToTile(latMax, lngMin, zoom);
    const tileSE = latLngToTile(latMin, lngMax, zoom);

    const tiles = [];
    for (let tx = tileNW.x; tx <= tileSE.x; tx++) {
      for (let ty = tileNW.y; ty <= tileSE.y; ty++) {
        // Tile NW and SE corners in lat/lng → SVG coords
        const nwLL = tileToLatLng(tx, ty, zoom);
        const seLL = tileToLatLng(tx + 1, ty + 1, zoom);
        const nwSVG = projectGeoPoint(nwLL.lat, nwLL.lng, transform);
        const seSVG = projectGeoPoint(seLL.lat, seLL.lng, transform);

        tiles.push({
          key: `${zoom}/${tx}/${ty}`,
          url: `https://tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`,
          x: nwSVG.x,
          y: nwSVG.y,
          width: seSVG.x - nwSVG.x,
          height: seSVG.y - nwSVG.y,
        });
      }
    }
    return tiles;
  }, [showMap, projection, width, height]);

  if (markIds.length === 0 || Object.keys(positions).length === 0) {
    return (
      <svg width={width} height={height} className="course-preview">
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#9ca3af" fontSize="13">
          No marks with coordinates
        </text>
      </svg>
    );
  }

  // Wind sector wedge from first mark (only for non-fixed start)
  const isFixedStart = startFinish && startFinish.startType === 'fixed';
  const windSector = (() => {
    if (isFixedStart) return null;
    if (typeof bearing !== 'number' || sequencePositions.length === 0) return null;
    const firstPos = sequencePositions[0];
    const downwindRad = ((bearing + 180) % 360 - 90) * Math.PI / 180;
    const sectorRadius = Math.min(width, height) * 0.2;
    const halfAngle = 11.25 * Math.PI / 180;

    const x1 = firstPos.x + sectorRadius * Math.cos(downwindRad - halfAngle);
    const y1 = firstPos.y + sectorRadius * Math.sin(downwindRad - halfAngle);
    const x2 = firstPos.x + sectorRadius * Math.cos(downwindRad + halfAngle);
    const y2 = firstPos.y + sectorRadius * Math.sin(downwindRad + halfAngle);

    return (
      <path
        d={`M${firstPos.x},${firstPos.y} L${x1.toFixed(1)},${y1.toFixed(1)} A${sectorRadius},${sectorRadius} 0 0,1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`}
        fill="rgba(99,102,241,0.1)"
        stroke="rgba(99,102,241,0.2)"
        strokeWidth="0.5"
      />
    );
  })();

  // --- Render helper: get position for a sequence index ---
  function seqPos(i) {
    return positions[markIds[i]];
  }

  // Start/finish icon positions — must match StartFinishLine rendering
  function sfIconPos(data) {
    if (data.iconPos) return { ...data.iconPos };
    return { x: (data.p1.x + data.p2.x) / 2, y: (data.p1.y + data.p2.y) / 2 };
  }
  const startMid = startFinishLines.start ? sfIconPos(startFinishLines.start) : null;
  const finishMid = startFinishLines.finish ? sfIconPos(startFinishLines.finish) : null;

  // Start/finish connector geometry. The rounding arc stays tangent-to-tangent;
  // for the start/finish side we honour the RHUMB tangent (a tangent parallel to
  // the configured offset/wind bearing) rather than the belt tangent aimed at
  // the nearby line — that belt tangent had a large angular variance from the
  // rhumb and looked like a swerve. The leg runs along the rhumb from that
  // tangent point and is extended to wherever it crosses the start/finish line;
  // if it would miss the drawn line, the line itself is extended to meet it
  // (never snapped to the centre or the ends). The icon/label stay at the
  // original midpoint. (ra-etwi #3)
  const lastSeqIdx = markIds.length - 1;
  const FAR = 1e6; // far point → belt tangent gamma ≈ 90° → tangent ∥ rhumb
  function sfConnector(line, seqIdx, dir) {
    const markPos = seqPos(seqIdx);
    if (!line || !markPos) return null;
    const hasArc = dir === 'port' || dir === 'starboard';
    const axis = sfAxis(line, markPos);
    if (!hasArc) {
      // No ring: the rhumb leg runs straight to the mark centre.
      const hit = lineLineIntersect(markPos, axis, line.p1, line.p2);
      return { ring: markPos, attach: hit ? hit.point : markPos, angle: null, lineRender: line };
    }
    const r = isBelt ? ringRadius(visitCounts[seqIdx]) : arcRadius(visitCounts[seqIdx]);
    const isArrival = seqIdx === 0; // start arrives at first mark; finish departs last
    const far = { x: markPos.x + FAR * axis.x, y: markPos.y + FAR * axis.y };
    const tan = beltTangent(markPos, far, r, dir === 'port', isArrival);
    const hit = lineLineIntersect(tan.point, axis, line.p1, line.p2);
    const attach = hit ? hit.point : tan.point;
    // Extend the rendered line when the leg meets it beyond an end.
    let lineRender = line;
    if (hit && (hit.s < 0 || hit.s > 1)) {
      const mid = sfIconPos(line);
      lineRender = hit.s > 1
        ? { ...line, p2: attach, iconPos: line.iconPos || mid }
        : { ...line, p1: attach, iconPos: line.iconPos || mid };
    }
    return { ring: tan.point, attach, angle: tan.angle, lineRender };
  }
  const startConn = sfConnector(startFinishLines.start, 0, (markDirections || [])[0]);
  const finishConn = sfConnector(startFinishLines.finish, lastSeqIdx, (markDirections || [])[lastSeqIdx]);
  const startLineRender = startConn ? startConn.lineRender : startFinishLines.start;
  const finishLineRender = finishConn ? finishConn.lineRender : startFinishLines.finish;

  // Extend turn angles with first/last marks using start/finish positions
  // (SVG-space approximation — equirectangular projection preserves angles locally)
  const turnAngles = [...baseTurnAngles];
  if (startConn && markIds.length >= 2 && sequencePositions.length >= 2) {
    const firstPos = sequencePositions[0];
    const secondPos = sequencePositions[1];
    const inBrg = Math.atan2(firstPos.x - startConn.attach.x, startConn.attach.y - firstPos.y) * 180 / Math.PI;
    const outBrg = Math.atan2(secondPos.x - firstPos.x, firstPos.y - secondPos.y) * 180 / Math.PI;
    turnAngles[0] = computeTurnAngle(inBrg, outBrg);
  }
  if (finishConn && sequencePositions.length >= 2) {
    const lastIdx = sequencePositions.length - 1;
    const lastPos = sequencePositions[lastIdx];
    const prevPos = sequencePositions[lastIdx - 1];
    const inBrg = Math.atan2(lastPos.x - prevPos.x, prevPos.y - lastPos.y) * 180 / Math.PI;
    const outBrg = Math.atan2(finishConn.attach.x - lastPos.x, lastPos.y - finishConn.attach.y) * 180 / Math.PI;
    turnAngles[lastIdx] = computeTurnAngle(inBrg, outBrg);
  }

  return (
    <svg width={width} height={height} className="course-preview">
      {/* Faded map tile background */}
      {showMap && mapTiles.length > 0 && (
        <g opacity="0.35" style={{ filter: 'saturate(0.3)' }}>
          {mapTiles.map((tile) => (
            <image
              key={tile.key}
              href={tile.url}
              x={tile.x} y={tile.y}
              width={tile.width} height={tile.height}
              preserveAspectRatio="none"
            />
          ))}
        </g>
      )}

      {/* Wind sector */}
      {windSector}

      {/* Start line (extended to meet the leg when needed) */}
      {startLineRender && (
        <StartFinishLine data={startLineRender} color="#f59e0b" label="Start" isStart />
      )}

      {/* Finish line (extended to meet the leg when needed) */}
      {finishLineRender && (
        <StartFinishLine data={finishLineRender} color="#3b82f6" label="Finish"
          isStart={false} dashOffset={startFinishLines.finish.dashOffset || 0} />
      )}

      {/* Start → first mark leg (rhumb tangent: along the wind, tangent to the
          ring, extended to cross the start line). (ra-etwi #3) */}
      {startConn && (() => {
        const o = opacityForIndex(0, animHead, animBlend, markIds.length, highlightedIndex);
        return (
          <g key="leg-start" opacity={o}>
            <line x1={startConn.attach.x} y1={startConn.attach.y} x2={startConn.ring.x} y2={startConn.ring.y}
              stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
            <ArrowHead p1={startConn.attach} p2={startConn.ring} />
          </g>
        );
      })()}

      {/* Leg lines */}
      {markIds.slice(0, -1).map((_, i) => {
        const fromPos = seqPos(i);
        const toPos = seqPos(i + 1);
        if (!fromPos || !toPos) return null;

        const fromDir = (markDirections || [])[i];
        const toDir = (markDirections || [])[i + 1];
        const fromHasArc = fromDir === 'port' || fromDir === 'starboard';
        const toHasArc = toDir === 'port' || toDir === 'starboard';

        let lineFrom = fromPos;
        let lineTo = toPos;

        if (isBelt) {
          if (fromHasArc) {
            const r = ringRadius(visitCounts[i]);
            lineFrom = beltTangent(fromPos, toPos, r, fromDir === 'port', false).point;
          }
          if (toHasArc) {
            const r = ringRadius(visitCounts[i + 1]);
            lineTo = beltTangent(toPos, fromPos, r, toDir === 'port', true).point;
          }
        } else {
          if (fromHasArc) {
            const r = arcRadius(visitCounts[i]);
            lineFrom = tangentPoint(fromPos, toPos, r);
          }
          if (toHasArc) {
            const r = arcRadius(visitCounts[i + 1]);
            const angle = Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x);
            lineTo = { x: toPos.x + r * Math.cos(angle), y: toPos.y + r * Math.sin(angle) };
          }
        }

        // Skip perpendicular offset when endpoints connect to arcs (belt rings handle separation)
        const offset = (isBelt && (fromHasArc || toHasArc)) ? 0 : segmentOffsets[i];
        const [p1, p2] = offsetLinePoints(lineFrom, lineTo, offset);
        // Leg faded while arc draws, fades in once arc completes
        const sf = sweepFractionForIndex(i, animHead, animBlend, highlightedIndex);
        const baseO = opacityForIndex(i, animHead, animBlend, markIds.length, highlightedIndex);
        let o;
        if (sf >= 1) {
          // Arc done — use normal opacity (may fade in from MIN via baseO)
          o = baseO;
        } else if (sf > 0) {
          // Arc drawing — lerp from MIN_OPACITY to baseO as arc completes
          o = MIN_OPACITY + (baseO - MIN_OPACITY) * sf;
        } else {
          // Future — show at MIN_OPACITY
          o = Math.min(baseO, MIN_OPACITY);
        }

        return (
          <g key={`leg-${i}`} opacity={o}>
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
            <ArrowHead p1={p1} p2={p2} />
          </g>
        );
      })}

      {/* Last mark → finish leg (rhumb tangent: leaves the ring along the wind,
          extended to cross the finish line). (ra-etwi #3) */}
      {finishConn && (() => {
        const o = opacityForIndex(markIds.length - 1, animHead, animBlend, markIds.length, highlightedIndex);
        return (
          <g key="leg-finish" opacity={o}>
            <line x1={finishConn.ring.x} y1={finishConn.ring.y} x2={finishConn.attach.x} y2={finishConn.attach.y}
              stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
            <ArrowHead p1={finishConn.ring} p2={finishConn.attach} />
          </g>
        );
      })()}

      {/* Rounding arcs (including first/last marks using start/finish positions) */}
      {markIds.map((id, i) => {
        const direction = (markDirections || [])[i];
        if (direction !== 'port' && direction !== 'starboard') return null;

        const markPos = seqPos(i);
        if (!markPos) return null;

        // The arc stays tangent-to-tangent. For the first/last mark the
        // start/finish side uses the RHUMB tangent (computed in startConn /
        // finishConn) so the arc honours the true angle, not the belt tangent.
        const isFirst = i === 0;
        const isLast = i === markIds.length - 1;
        const prevPos = i > 0 ? seqPos(i - 1) : (startConn ? startConn.ring : startMid);
        const nextPos = i < markIds.length - 1 ? seqPos(i + 1) : (finishConn ? finishConn.ring : finishMid);
        if (!prevPos || !nextPos) return null;

        const isPort = direction === 'port';

        const sf = sweepFractionForIndex(i, animHead, animBlend, highlightedIndex);
        // Draw full arc for future/passed marks (dimmed via opacity), partial only while actively sweeping
        const drawSf = sf > 0 && sf < 1 ? sf : 1;

        let d;
        if (isBelt) {
          const radius = ringRadius(visitCounts[i]);
          const arrivalAngle = (isFirst && startConn)
            ? startConn.angle
            : beltTangent(markPos, prevPos, radius, isPort, true).angle;
          const departureAngle = (isLast && finishConn)
            ? finishConn.angle
            : beltTangent(markPos, nextPos, radius, isPort, false).angle;
          d = beltArcPath(markPos, radius, arrivalAngle, departureAngle, isPort, drawSf);
        } else {
          const radius = arcRadius(visitCounts[i]);
          // Classic arc normally arrives facing the reflected previous point; at
          // the first/last mark it faces the rhumb-tangent ring point directly
          // so it lines up with the start/finish leg. (ra-etwi #3)
          const arrFrom = (isFirst && startConn)
            ? startConn.ring
            : { x: 2 * markPos.x - prevPos.x, y: 2 * markPos.y - prevPos.y };
          const arcNext = (isLast && finishConn) ? finishConn.ring : nextPos;
          d = roundingArcPath(arrFrom, markPos, arcNext, isPort, radius, drawSf);
        }
        if (!d) return null;

        const baseArcO = opacityForIndex(i, animHead, animBlend, markIds.length, highlightedIndex);
        const arcO = baseArcO < 0.7 ? baseArcO : 0.7;

        return (
          <path
            key={`arc-${i}`}
            d={d}
            fill="none"
            stroke={isPort ? '#dc2626' : '#16a34a'}
            strokeWidth={ARC_STROKE}
            strokeLinecap="round"
            opacity={arcO}
          />
        );
      })}

      {/* Turn angle annotations */}
      {showAngles && markIds.map((id, i) => {
        const angle = turnAngles[i];
        if (angle === null) return null;
        const pos = seqPos(i);
        if (!pos) return null;

        const dir = (markDirections || [])[i];
        const hasArc = dir === 'port' || dir === 'starboard';

        // Adjust angle to match rounding direction (may exceed ±180°)
        let adjusted = angle;
        if (dir === 'port' && angle > 0) adjusted = angle - 360;
        else if (dir === 'starboard' && angle < 0) adjusted = angle + 360;
        const absDeg = Math.abs(adjusted);

        // >180° effective turn shown in amber
        const color = absDeg > 180 ? '#d97706' : '#475569';

        const outerR = hasArc
          ? (isBelt ? ringRadius(visitCounts[i]) : arcRadius(visitCounts[i]))
          : MARK_RADIUS;

        const label = `${Math.round(absDeg)}°`;
        // Match the outgoing leg opacity (sf gated), scaled down so bold text
        // perceptually matches thin lines at the same nominal opacity
        const sf = sweepFractionForIndex(i, animHead, animBlend, highlightedIndex);
        const baseO = opacityForIndex(i, animHead, animBlend, markIds.length, highlightedIndex);
        const legO = sf >= 1 ? baseO : sf > 0 ? MIN_OPACITY + (baseO - MIN_OPACITY) * sf : Math.min(baseO, MIN_OPACITY);
        const angleO = legO < 1 ? legO * 0.6 : legO;

        return (
          <text
            key={`angle-${i}`}
            x={pos.x}
            y={pos.y - outerR - 6}
            textAnchor="middle"
            fontSize={ANGLE_FONT_SIZE}
            fontWeight="600"
            fill={color}
            opacity={angleO}
          >
            {label}
          </text>
        );
      })}

      {/* Mark circles (clickable) */}
      {Object.entries(positions).map(([id, pos]) => {
        const mark = marks.find((m) => m.id === id);
        const fill = mark ? markColor(mark.appearance) : '#6b7280';
        const seqIdx = markIds.indexOf(id);
        const markO = opacityForMark(id, markIds, animHead, animBlend, highlightedIndex);

        return (
          <g
            key={`mark-${id}`}
            style={{ cursor: onMarkClick ? 'pointer' : 'default' }}
            onClick={() => onMarkClick && onMarkClick(id, seqIdx)}
            opacity={markO}
          >
            <circle
              cx={pos.x} cy={pos.y} r={MARK_RADIUS}
              fill={fill}
              stroke="#fff"
              strokeWidth="2"
              opacity="0.9"
            />
            <text
              x={pos.x} y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={LABEL_FONT_SIZE}
              fontWeight="700"
              fill="#fff"
            >
              {id.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* Map toggle button */}
      <g
        style={{ cursor: 'pointer' }}
        onClick={() => setShowMap(!showMap)}
      >
        <rect
          x={4} y={height - 28}
          width="38" height="18" rx="4"
          fill={showMap ? '#e0e7ff' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="0.5"
        />
        <text
          x={23} y={height - 16}
          textAnchor="middle" dominantBaseline="central"
          fontSize="9" fill={showMap ? '#4338ca' : '#64748b'}
        >
          map
        </text>
      </g>

      {/* Play once button */}
      {markIds.length >= 2 && (
        <g
          style={{ cursor: 'pointer' }}
          onClick={() => { onClearHighlight?.(); if (!playing) startAnim(false); }}
        >
          <rect
            x={46} y={height - 28}
            width="22" height="18" rx="4"
            fill={playing && !autoplay ? '#e0e7ff' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="0.5"
          />
          <polygon
            points={`${53},${height - 22} ${53},${height - 12} ${63},${height - 17}`}
            fill={playing && !autoplay ? '#4338ca' : '#64748b'}
          />
        </g>
      )}

      {/* Autoplay toggle button */}
      {markIds.length >= 2 && (
        <g
          style={{ cursor: 'pointer' }}
          onClick={() => { onClearHighlight?.(); setAutoplay(!autoplay); }}
        >
          <rect
            x={72} y={height - 28}
            width="22" height="18" rx="4"
            fill={autoplay ? '#e0e7ff' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="0.5"
          />
          <text
            x={83} y={height - 16}
            textAnchor="middle" dominantBaseline="central"
            fontSize="10" fill={autoplay ? '#4338ca' : '#64748b'}
          >
            ↻
          </text>
        </g>
      )}

      {/* Wind rose (top-right corner) */}
      {typeof bearing === 'number' && (
        <WindRose x={width - 54} y={4} bearing={bearing} />
      )}

      {/* Angles toggle button */}
      <g
        style={{ cursor: 'pointer' }}
        onClick={() => setShowAngles(!showAngles)}
      >
        <rect
          x={width - 112} y={height - 28}
          width="34" height="18" rx="4"
          fill={showAngles ? '#e0e7ff' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="0.5"
        />
        <text
          x={width - 95} y={height - 16}
          textAnchor="middle" dominantBaseline="central"
          fontSize="9" fill={showAngles ? '#4338ca' : '#64748b'}
        >
          deg
        </text>
      </g>

      {/* Style toggle button */}
      {markIds.length >= 3 && (
        <g
          style={{ cursor: 'pointer' }}
          onClick={() => setMode(mode === 'belt' ? 'classic' : 'belt')}
        >
          <rect
            x={width - 74} y={height - 28}
            width="70" height="18" rx="4"
            fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="0.5"
          />
          <text
            x={width - 39} y={height - 16}
            textAnchor="middle" dominantBaseline="central"
            fontSize="9" fill="#64748b"
          >
            {isBelt ? 'belt' : 'ground track'}
          </text>
        </g>
      )}

      {/* OSM attribution (required when map shown) */}
      {showMap && (
        <text
          x={width - 4} y={height - 4}
          textAnchor="end" fontSize="7" fill="#94a3b8" opacity="0.7"
        >
          © OpenStreetMap
        </text>
      )}
    </svg>
  );
}

const SF_MARK_RADIUS = 4;
const SF_CIRCLE_RADIUS = MARK_RADIUS * 0.55;
const SF_ICON_SIZE = 6;

/** Start or finish line with play/stop icon in a half-size circle.
 *  Same line → icon at one end (iconPos). Different lines → icon at midpoint. */
function StartFinishLine({ data, color, label, dashOffset = 0, isStart }) {
  const mx = data.iconPos ? data.iconPos.x : (data.p1.x + data.p2.x) / 2;
  const my = data.iconPos ? data.iconPos.y : (data.p1.y + data.p2.y) / 2;
  return (
    <g>
      {/* Dashed line */}
      <line
        x1={data.p1.x} y1={data.p1.y}
        x2={data.p2.x} y2={data.p2.y}
        stroke={color}
        strokeWidth="2"
        strokeDasharray="5,3"
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
      />
      {/* Fixed-line mark dots */}
      {data.mode === 'fixed' && data.markA && (
        <circle cx={data.markA.pos.x} cy={data.markA.pos.y} r={SF_MARK_RADIUS}
          fill={color} stroke="#fff" strokeWidth="1" />
      )}
      {data.mode === 'fixed' && data.markB && (
        <circle cx={data.markB.pos.x} cy={data.markB.pos.y} r={SF_MARK_RADIUS}
          fill={color} stroke="#fff" strokeWidth="1" />
      )}
      {/* Circle with play ▶ or stop ■ icon */}
      <circle cx={mx} cy={my} r={SF_CIRCLE_RADIUS}
        fill={color} stroke="#fff" strokeWidth="2" opacity="0.9" />
      {isStart ? (
        <polygon
          points={`${mx - SF_ICON_SIZE * 0.3},${my - SF_ICON_SIZE * 0.5} ${mx + SF_ICON_SIZE * 0.5},${my} ${mx - SF_ICON_SIZE * 0.3},${my + SF_ICON_SIZE * 0.5}`}
          fill="#fff"
        />
      ) : (
        <rect
          x={mx - SF_ICON_SIZE * 0.35} y={my - SF_ICON_SIZE * 0.35}
          width={SF_ICON_SIZE * 0.7} height={SF_ICON_SIZE * 0.7}
          rx="1" fill="#fff"
        />
      )}
      <title>{label}</title>
    </g>
  );
}

/** Arrow head at leg midpoint showing direction. */
function ArrowHead({ p1, p2 }) {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  const len = 8;       // tip-to-base length
  const halfW = 3.5;   // half-width at base

  const tipX = mx + len * 0.5 * Math.cos(angle);
  const tipY = my + len * 0.5 * Math.sin(angle);
  const baseX = mx - len * 0.5 * Math.cos(angle);
  const baseY = my - len * 0.5 * Math.sin(angle);
  const nx = -Math.sin(angle);
  const ny = Math.cos(angle);

  return (
    <polygon
      points={`${tipX},${tipY} ${baseX + nx * halfW},${baseY + ny * halfW} ${baseX - nx * halfW},${baseY - ny * halfW}`}
      fill="#64748b"
      opacity="0.8"
    />
  );
}

/** Compact wind rose showing the active wind direction sector. */
function WindRose({ x, y, bearing }) {
  const size = 50;
  const cx = x + size / 2;
  const cy = y + size / 2;
  const outerR = 22;
  const sectorHalf = 360 / 16 / 2;

  // Find which direction letter matches this bearing
  const activeDir = DIRECTION_LETTERS.find((letter) => {
    const db = DIRECTION_BEARINGS[letter];
    let diff = Math.abs(bearing - db);
    if (diff > 180) diff = 360 - diff;
    return diff <= sectorHalf;
  });

  return (
    <g opacity="0.7">
      <circle cx={cx} cy={cy} r={outerR + 2}
        fill="rgba(255,255,255,0.8)" stroke="#ccc" strokeWidth="0.5" />
      {/* Active sector */}
      {activeDir && (() => {
        const db = DIRECTION_BEARINGS[activeDir];
        const startAngle = (db - sectorHalf - 90) * Math.PI / 180;
        const endAngle = (db + sectorHalf - 90) * Math.PI / 180;
        const x1 = cx + outerR * Math.cos(startAngle);
        const y1 = cy + outerR * Math.sin(startAngle);
        const x2 = cx + outerR * Math.cos(endAngle);
        const y2 = cy + outerR * Math.sin(endAngle);
        return (
          <path
            d={`M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${outerR},${outerR} 0 0,1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`}
            fill="rgba(99,102,241,0.25)"
            stroke="rgba(99,102,241,0.5)"
            strokeWidth="0.5"
          />
        );
      })()}
      {/* Tick marks */}
      {DIRECTION_LETTERS.map((letter) => {
        const db = DIRECTION_BEARINGS[letter];
        const angle = (db - 90) * Math.PI / 180;
        const r1 = outerR - 3;
        return (
          <line key={letter}
            x1={cx + r1 * Math.cos(angle)} y1={cy + r1 * Math.sin(angle)}
            x2={cx + outerR * Math.cos(angle)} y2={cy + outerR * Math.sin(angle)}
            stroke="#999" strokeWidth="0.5"
          />
        );
      })}
      {/* Cardinal labels */}
      {[
        { label: 'N', angle: -90 },
        { label: 'E', angle: 0 },
        { label: 'S', angle: 90 },
        { label: 'W', angle: 180 },
      ].map(({ label, angle }) => {
        const r = outerR - 9;
        const rad = angle * Math.PI / 180;
        return (
          <text key={label}
            x={cx + r * Math.cos(rad)} y={cy + r * Math.sin(rad)}
            textAnchor="middle" dominantBaseline="central"
            fontSize="7" fontWeight="700" fill="#333"
          >
            {label}
          </text>
        );
      })}
      <circle cx={cx} cy={cy} r={1.5} fill="#666" />
    </g>
  );
}
